/**
 * Notification bell — dropdown that shows the user's recent in-app
 * notifications and unread count badge.
 *
 * Self-contained: fetches its own data on mount and on a 60s poll
 * cadence (cheap — single index hit on notifications_user_unread_idx).
 * Marks notifications read on click and routes to their `link` if set.
 *
 * Renders three trigger layouts via the `variant` prop so the same
 * data-fetching hook + dropdown can mount in the legacy header,
 * the expanded sidebar (full-width row), or the collapsed sidebar
 * (icon-only). The dropdown anchors differently per variant so it
 * never gets clipped by the sidebar's narrow width:
 *   - header              → absolute right-0 mt-2 (under the bell)
 *   - sidebar-expanded    → absolute left-full top-0 ml-2 (right of sidebar)
 *   - sidebar-collapsed   → absolute left-full top-0 ml-2 (right of sidebar)
 *
 * Backend contract (server/routes/notifications.js):
 *   GET  /api/notifications?limit=20            → { items, unreadCount }
 *   PATCH /api/notifications/:id/read           → { id, readAt, unreadCount }
 *   POST /api/notifications/mark-all-read       → { marked, unreadCount }
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, BellRing, CheckCheck, AlertTriangle, Info, AlertCircle } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
const POLL_INTERVAL_MS = 60_000;

type Severity = 'info' | 'warn' | 'critical';
type Category = 'billing' | 'quota' | 'addon' | 'plan' | 'tracker' | 'system' | 'admin';

interface NotificationItem {
  id: string;
  type: string;
  severity: Severity;
  title: string;
  body: string | null;
  link: string | null;
  metadata: Record<string, unknown>;
  read_at: string | null;
  created_at: string;
  // Phase 10: server-side template registry. When `template_key` is
  // present and we have a matching client i18n entry, we re-render
  // title/body via i18next so a user who flips their UI language
  // sees historical notifications in the new language. Otherwise
  // fall back to the server-rendered `title`/`body`.
  template_key?: string | null;
  template_vars?: Record<string, unknown> | null;
  category?: Category | null;
  action_url?: string | null;
}

// Tailwind palette per category. Hard-coded so the palette stays
// consistent if a future server-only category sneaks through; we
// fall back to a neutral chip rather than rendering raw text.
const CATEGORY_CHIP_CLASS: Record<Category, string> = {
  billing: 'bg-purple-100 text-purple-700 border-purple-200',
  quota: 'bg-amber-100 text-amber-700 border-amber-200',
  addon: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  plan: 'bg-blue-100 text-blue-700 border-blue-200',
  tracker: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  system: 'bg-slate-100 text-slate-700 border-slate-200',
  admin: 'bg-pink-100 text-pink-700 border-pink-200',
};

export type NotificationBellVariant = 'header' | 'sidebar-expanded' | 'sidebar-collapsed';

interface NotificationBellProps {
  variant?: NotificationBellVariant;
}

function authHeader(): HeadersInit {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function severityIcon(severity: Severity) {
  if (severity === 'critical') return <AlertCircle className="w-4 h-4 text-red-500" />;
  if (severity === 'warn') return <AlertTriangle className="w-4 h-4 text-amber-500" />;
  return <Info className="w-4 h-4 text-blue-500" />;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ variant = 'header' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/notifications?limit=20`, {
        headers: { ...authHeader() },
      });
      if (!res.ok) return;
      const json = await res.json();
      setItems(Array.isArray(json.items) ? json.items : []);
      setUnread(Number(json.unreadCount) || 0);
    } catch (err) {
      console.warn('[notifications] load failed', err);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_INTERVAL_MS);
    // Refresh when the tab regains focus — most users alt-tab back
    // expecting the unread count to be current, and the 60s timer
    // can leave a stale state for up to a minute otherwise.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  // Click-outside dismiss.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleItemClick = useCallback(
    async (item: NotificationItem) => {
      // Optimistic — flip read locally first.
      setItems((prev) =>
        prev.map((n) => (n.id === item.id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n))
      );
      if (!item.read_at) {
        setUnread((prev) => Math.max(0, prev - 1));
        try {
          await fetch(`${API_URL}/notifications/${item.id}/read`, {
            method: 'PATCH',
            headers: { ...authHeader() },
          });
        } catch (err) {
          console.warn('[notifications] mark-read failed', err);
        }
      }
      // Phase 10: prefer the new `action_url` column over the legacy
      // `link`. Server mirrors action_url into link on insert so this
      // is just defence in depth for older rows.
      const target = item.action_url || item.link;
      if (target) {
        setOpen(false);
        navigate(target);
      }
    },
    [navigate]
  );

  /**
   * Render the title/body via i18n if the row has a template_key AND
   * a matching client-side i18n entry. Otherwise fall back to the
   * server-rendered text. Doing this per-row (instead of in `load`)
   * means a user who flips their language preference re-renders
   * historical bell items immediately, no refetch needed.
   */
  const renderText = useCallback(
    (item: NotificationItem): { title: string; body: string | null } => {
      if (!item.template_key) {
        return { title: item.title, body: item.body };
      }
      const titleKey = `notifications.templates.${item.template_key}.title`;
      const bodyKey = `notifications.templates.${item.template_key}.body`;
      const vars = (item.template_vars || {}) as Record<string, unknown>;
      // i18next returns the key itself when the entry is missing —
      // detect that and fall back to the server text rather than
      // showing "notifications.templates.foo.title" in the UI.
      const titleCandidate = t(titleKey, { ...vars, defaultValue: '' });
      const bodyCandidate = t(bodyKey, { ...vars, defaultValue: '' });
      // Some templates have a body-with-variant fallback (e.g. addon
      // activated without an expiry). The server already chose; we
      // just render whatever we got, falling back to the persisted
      // body if the i18n lookup is empty.
      return {
        title: titleCandidate || item.title,
        body: bodyCandidate || item.body,
      };
    },
    [t],
  );

  const handleMarkAll = useCallback(async () => {
    if (unread === 0) return;
    setLoading(true);
    setItems((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() }))
    );
    setUnread(0);
    try {
      await fetch(`${API_URL}/notifications/mark-all-read`, {
        method: 'POST',
        headers: { ...authHeader() },
      });
    } catch (err) {
      console.warn('[notifications] mark-all failed', err);
    } finally {
      setLoading(false);
    }
  }, [unread]);

  const formatRelativeTime = useCallback(
    (iso: string): string => {
      const then = new Date(iso).getTime();
      const diff = Date.now() - then;
      const min = Math.floor(diff / 60_000);
      if (min < 1) return t('notifications.relativeTime.justNow');
      if (min < 60) return t('notifications.relativeTime.minutesAgo', { n: min });
      const hr = Math.floor(min / 60);
      if (hr < 24) return t('notifications.relativeTime.hoursAgo', { n: hr });
      const day = Math.floor(hr / 24);
      if (day < 7) return t('notifications.relativeTime.daysAgo', { n: day });
      return new Date(iso).toLocaleDateString();
    },
    [t]
  );

  const Icon = unread > 0 ? BellRing : Bell;
  const titleLabel = t('notifications.title');
  const toggleOpen = () => {
    setOpen((v) => !v);
    if (!open) load();
  };

  // Dropdown anchoring differs per variant. In sidebar mode we
  // anchor to the RIGHT of the wrapper so the panel sits beside the
  // sidebar — anchoring below would either overflow the narrow
  // `aside` or get clipped by `nav`'s overflow-y-auto.
  const dropdownPositionClass =
    variant === 'header'
      ? 'absolute right-0 mt-2'
      : 'absolute left-full top-0 ml-2';

  // Wrapper width affects how `left-full` resolves. For sidebar
  // variants we want the wrapper to fill the row so the dropdown
  // anchors at the sidebar's right edge, not somewhere mid-row.
  const wrapperClass =
    variant === 'sidebar-expanded'
      ? 'relative w-full'
      : variant === 'sidebar-collapsed'
      ? 'relative flex justify-center'
      : 'relative';

  const renderTrigger = () => {
    if (variant === 'sidebar-expanded') {
      const emphasised = unread > 0;
      return (
        <button
          type="button"
          onClick={toggleOpen}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all relative ${
            emphasised
              ? 'bg-amber-50/60 border-l-2 border-amber-500 text-gray-800 hover:bg-amber-100/70'
              : 'text-gray-700 hover:bg-gray-100'
          }`}
          aria-label={titleLabel}
          aria-expanded={open}
        >
          <span className={emphasised ? 'text-amber-600' : 'text-gray-500'}>
            <Icon className="w-5 h-5" />
          </span>
          <span className="flex-1 text-left">
            <span className="block font-medium">{titleLabel}</span>
            {emphasised && (
              <span className="block text-[11px] text-amber-700 mt-0.5">
                {t('notifications.sidebarSublabelNew', { count: unread })}
              </span>
            )}
          </span>
          {unread > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      );
    }
    // header + sidebar-collapsed share the same icon-button look.
    // The corner badge keeps the design language identical so users
    // who collapse the sidebar still recognise it instantly.
    return (
      <button
        type="button"
        onClick={toggleOpen}
        className="relative p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        aria-label={titleLabel}
        aria-expanded={open}
        title={variant === 'sidebar-collapsed' ? titleLabel : undefined}
      >
        <Icon className={`w-5 h-5 ${unread > 0 ? 'text-amber-600' : ''}`} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    );
  };

  return (
    <div ref={wrapperRef} className={wrapperClass}>
      {renderTrigger()}

      {open && (
        <div
          className={`${dropdownPositionClass} w-80 sm:w-96 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden`}
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{titleLabel}</h3>
              <p className="text-xs text-gray-500">
                {unread === 0
                  ? t('notifications.allCaughtUp')
                  : t('notifications.unreadCount', { count: unread })}
              </p>
            </div>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={loading || unread === 0}
              className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              {t('notifications.markAllRead')}
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{t('notifications.empty.title')}</p>
                <p className="text-xs text-gray-400 mt-1">{t('notifications.empty.body')}</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {items.map((item) => {
                  const isUnread = !item.read_at;
                  const { title, body } = renderText(item);
                  const category = item.category || null;
                  const target = item.action_url || item.link || null;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handleItemClick(item)}
                        className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 transition-colors ${
                          isUnread ? 'bg-blue-50/40' : ''
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">{severityIcon(item.severity)}</div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'} truncate`}>
                            {title}
                          </p>
                          {body && (
                            <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{body}</p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {category && CATEGORY_CHIP_CLASS[category] && (
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold ${CATEGORY_CHIP_CLASS[category]}`}
                              >
                                {t(`notifications.categories.${category}`, { defaultValue: category })}
                              </span>
                            )}
                            <span className="text-[11px] text-gray-400">
                              {formatRelativeTime(item.created_at)}
                            </span>
                            {target && (
                              <span className="text-[11px] font-medium text-blue-600 ml-auto">
                                {t('notifications.view')}
                              </span>
                            )}
                          </div>
                        </div>
                        {isUnread && (
                          <span className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-blue-500" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
